<template>
  <BaseDialog
    :title="$t('coaching.feedbackGoals.createDialog.title')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="emit('close-dialog')"
  >
    <LoadingWrapper :state="templateLoadingStatus">
      <ul class="flex flex-col">
        <template v-if="step === 0">
          <p v-if="errors.get('noSupportedPerspectives')" class="text-error text-body-small mb-2">
            {{ errors.get('noSupportedPerspectives') }}
          </p>
          <SurveyMembersForm
            v-model:feedback-sender-ids="form.feedbackSenderIds"
            :members-error="errors.get('feedbackSenderIds')"
            :show-select-all-button="true"
            member-filter="coachees"
          />
          <DialogBreak />
          <FeedbackTopicList
            v-model="form.feedbackTopicId"
            :templates="filteredTemplates"
            :show-perspectives="true"
            :single-choice="true"
            :error="errors.get('feedbackTopicId')"
          />
        </template>
        <template v-else-if="step === 1">
          <p class="text-body-medium text-on-surface-variant mb-2">
            {{ $t('coaching.feedbackGoals.perspectiveCountDescription') }}
          </p>
          <p v-if="errors.get('perspectiveCounts')" class="text-error text-body-small mb-2">
            {{ errors.get('perspectiveCounts') }}
          </p>
          <p v-if="errors.get('noSupportedPerspectives')" class="text-error text-body-small mb-2">
            {{ errors.get('noSupportedPerspectives') }}
          </p>
          <div
            class="[&>div]:rounded-extra-small [&>div]:last:rounded-b-large [&>div]:first:rounded-t-large flex w-full flex-col gap-0.5"
          >
            <EvaluationPerspectiveGoalSetter
              v-for="p in goalPerspectives"
              :key="p.key"
              :is-active="true"
              :perspective="p"
              :label="labelFor(p)"
              :number-of-feedbacks="getPerspectiveCount(p)"
              @plus="adjustPerspectiveCount(p, 'add')"
              @minus="adjustPerspectiveCount(p, 'remove')"
              @update:number-of-feedbacks="setPerspectiveCount(p, $event)"
            />
          </div>
        </template>
      </ul>
    </LoadingWrapper>
    <template #confirm>
      <div class="flex flex-row gap-1">
        <BaseTextButton v-if="step > 0" color="text" size="M" @click="previousStep">{{
          $t('common.back')
        }}</BaseTextButton>
        <BaseTextButton
          v-if="step < 1"
          color="filled"
          size="M"
          :disabled="isSaving"
          @click="nextStep"
          >{{ $t('common.continue') }}</BaseTextButton
        >
        <BaseTextButton
          v-if="step === 1"
          color="filled"
          size="M"
          :disabled="isSaving"
          @click="saveGoal"
          >{{ $t('coaching.feedbackGoals.createDialog.create') }}</BaseTextButton
        >
      </div>
    </template>
    <template #dismiss>
      <BaseTextButton color="text" :disabled="isSaving" @click="emit('close-dialog')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import { computed, toRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import FeedbackTopicList from '@/core/components/feedback-topic-list/FeedbackTopicList.vue'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import SurveyMembersForm from '@/modules/start-survey/components/SurveyMembersForm.vue'
  import EvaluationPerspectiveGoalSetter from '@/modules/coachees/components/EvaluationPerspectiveGoalSetter.vue'
  import type { Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useCreateCoachFeedbackGoal } from '../composables/use-create-coach-feedback-goal'

  interface Props {
    isOpen: boolean
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'goal-created', goalId: string): void
  }>()

  const { t } = useI18n()

  const {
    step,
    nextStep,
    previousStep,
    saveGoal,
    form,
    errors,
    filteredTemplates,
    templateLoadingStatus,
    goalPerspectives,
    getPerspectiveCount,
    adjustPerspectiveCount,
    setPerspectiveCount,
    isSaving,
  } = useCreateCoachFeedbackGoal(toRef(props, 'isOpen'), (goalId) => emit('goal-created', goalId))

  const topicPerspectives = computed(
    () => filteredTemplates.value.find((t) => t.id === form.feedbackTopicId)?.perspectives ?? [],
  )

  const labelFor = (perspective: Perspective) =>
    resolvePerspectiveLabel(perspective.key, topicPerspectives.value, {
      kind: 'title',
      selfLabel: t('perspectives.self.default'),
    })
</script>
